/**
 * What each URL tells a search engine it is.
 *
 * One module rather than a `document.title` line per page: a title, a
 * description, a canonical URL and the social cards all have to agree, and the
 * only way to keep them agreeing is to write them once. `components/Seo.tsx`
 * puts what is here into the document head; `scripts/generate-sitemap.js`
 * reads the same city list to write `public/sitemap.xml`.
 *
 * The keyword cluster each page targets, and why, is documented in
 * ../../SEO.md — change the strategy there and the copy here together.
 */

import citiesData from '../data/cities.json';
import { SITE_ORIGIN } from './shareLinks';

export { SITE_ORIGIN };

/** How the brand is written. Never "CentMap". */
export const SITE_NAME = 'Centmap';

/**
 * The social card image.
 *
 * The square logo rather than a 1200x630 banner: it is the only image on the
 * site that is guaranteed to exist at a size the crawlers accept, and a
 * square is cropped predictably by all of them.
 */
export const SOCIAL_IMAGE = `${SITE_ORIGIN}/centmap-logo-1024-1024.jpeg`;

/**
 * Canonical profile URLs.
 *
 * These are the same ones in the `Organization.sameAs` graph in
 * public/index.html and in the footer — a profile that moves has to move in
 * all three, or the entity Google builds for Centmap comes apart.
 */
export const INSTAGRAM_URL = 'https://www.instagram.com/centmap/';
export const LINKEDIN_URL = 'https://www.linkedin.com/company/centmap';
export const FACEBOOK_URL = 'https://www.facebook.com/people/Centmap/61574713830701/';
export const X_URL = 'https://x.com/centmap_app';

export interface CitySeo {
  slug: string;
  name: string;
  country: string;
  currency: string;
  symbol: string;
  /** Sitemap priority, as a string so it round-trips into XML unchanged. */
  priority: string;
  /** Which search-intent cluster this page is written for. See SEO.md. */
  intent: string;
  description: string;
}

/** Every city the site has a map page for, in the order the footer lists them. */
export const CITIES: CitySeo[] = citiesData.cities;

export function citySeo(slug: string | undefined): CitySeo | undefined {
  if (!slug) return undefined;
  return CITIES.find((city) => city.slug === slug);
}

/** An absolute URL for a path, for canonicals and `og:url`. */
export function absoluteUrl(path: string): string {
  return path === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`;
}

export interface PageSeo {
  title: string;
  description: string;
  /** Root-relative, exactly as the router spells it. */
  path: string;
}

/**
 * Titles are written "Page | Centmap" so the distinguishing words survive the
 * ~60-character truncation in a result listing, and descriptions are kept
 * inside ~155 characters for the same reason.
 */
export const PAGE_SEO = {
  app: {
    title: 'Download the Centmap App — iOS & Android | Centmap',
    description:
      'Get Centmap free on iPhone and Android. Live beer prices, happy hour alerts and the whole map in your pocket, in every city Centmap covers.',
    path: '/app',
  },
  faq: {
    title: 'Centmap FAQ — Where the Prices Come From | Centmap',
    description:
      'How Centmap gets its beer prices, how to become an editor, how to add a bar, and what to do when a price on the map is wrong.',
    path: '/faq',
  },
  team: {
    title: 'The Team Behind Centmap | Centmap',
    description:
      'Who builds Centmap — the community-run live price map for beer and happy hours across Singapore, London, Bangkok, Hong Kong and more.',
    path: '/team',
  },
  support: {
    title: 'Contact & Feedback | Centmap',
    description:
      'Report a wrong price, request a city, ask to become an editor, or tell us what else you want the price of. We read every message.',
    path: '/support',
  },
  privacy: {
    title: 'Privacy Policy | Centmap',
    description:
      'What Centmap collects, what it does not, and how to control it — for the website and for the iOS and Android apps.',
    path: '/privacy-policy',
  },
} as const satisfies Record<string, PageSeo>;

/** Title and description for a city's map page. */
export function cityPageSeo(city: CitySeo): PageSeo {
  return {
    title: `Beer Prices & Happy Hours in ${city.name} — Live Map | Centmap`,
    description: city.description,
    path: `/${city.slug}`,
  };
}

/**
 * A city the site serves but has no copy for yet.
 *
 * Firestore, not this file, decides which cities exist, so a city can appear
 * on the backend before anyone writes its description. Better a generic page
 * that is indexable than a page with no description at all.
 */
export function fallbackCityPageSeo(name: string, slug: string): PageSeo {
  return {
    title: `Beer Prices & Happy Hours in ${name} — Live Map | Centmap`,
    description: `Live beer prices and happy hours across ${name}, updated by the people drinking them. Compare what a beer costs right now on the Centmap price map.`,
    path: `/${slug}`,
  };
}

/** The one URL on the site that is about a single venue. */
export function barPageSeo(barName: string, cityName: string | undefined, path: string): PageSeo {
  const where = cityName ? ` in ${cityName}` : '';
  return {
    title: `${barName}${where} — Beer Prices & Happy Hour | Centmap`,
    description: `Beer prices, happy hour times and opening hours for ${barName}${where}, kept current by the Centmap community. See what a drink costs there right now.`,
    path,
  };
}
